import { IContent } from "@vmosolutions/oc-interfaces";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useFileContents } from "src/pages/contentViewers/useFileContents";
import { FILE_PREVIEW_ERROR_MESSAGE } from "src/pages/contentViewers/ViewerShell";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { Document, Page } from "react-pdf";
import { Alert, Box, BoxProps, Button, ButtonGroup, Grid, styled, TextField } from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { useDispatch } from "react-redux";
// Configures the shared pdf.js worker; must be imported before any document is parsed.
import "src/utils/pdfWorker";

interface IPDFViewerProps {
  windowProps: {
    file: IContent;
  };
}

interface IPDFBoxPRops extends BoxProps {
  loadError?: boolean;
}

const PDFBox = styled(Box)<IPDFBoxPRops>(({ theme }) => ({
  width: "100%",
  "& .pdfViewerContainer": {
    display: "flex",
    height: "calc(100svh - 160px)",
    width: "100%",
    justifyContent: "space-between",

    "& .thumbnailSidebar": {
      width: "120px",
      overflowY: "auto",
      borderRight: `1px solid ${theme.palette.background.default}`,
      paddingTop: "20px",
      paddingRight: "20px",
      marginBottom: "20px",
      backgroundColor: theme.palette.background.default,

      "& .thumbnail": {
        marginBottom: "10px",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        fontSize: "0.9em",
        cursor: "pointer",
        transition: "transform 0.2s ease",
        "&.active": {
          color: theme.palette.grey[100],
        },
        "&:hover": {
          transform: "scale(1.05)",
        },
      },
    },
    "& .pdfMainViewer": {
      flex: 1,
      display: "flex",
      justifyContent: "center",
      alignItems: "top",
      overflow: "scroll",
      width: "calc(100% - 140px)",
      padding: "20px",
      marginBottom: "20px",
      overflowY: "auto:",
    },
    "& .pdfDocument": {
      width: "100%",
      height: "calc(100svh - 120px)",
    },
    "& .pdfPage": {
      width: "100%",
    },
  },
}));

export function PDFViewerWindow(props: IPDFViewerProps): JSX.Element {
  const { file } = props.windowProps;
  const { data: PDFData, loading, error: fetchError } = useFileContents(file, "arrayBuffer");
  // react-pdf can fail to render bytes that fetched fine, so track that separately from the fetch.
  const [renderError, setRenderError] = useState<boolean>(false);
  useEffect(() => {
    setRenderError(false);
  }, [PDFData, file._id]);
  const loadError = fetchError || renderError;
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setpageWidth] = useState<number | null>(580);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (pageRef.current && pageRef.current.getBoundingClientRect().width && !loading) {
      setpageWidth(pageRef.current.getBoundingClientRect().width - 160);
    }
  }, [pageRef, loading]);

  const memoizedFile = useMemo(() => (PDFData ? { data: PDFData } : null), [PDFData]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
  };
  return (
    <>
      {!loadError && (
        <PDFBox>
          {memoizedFile && !loading && (
            <Box ref={pageRef}>
              <Document
                file={memoizedFile}
                onLoadSuccess={onDocumentLoadSuccess}
                className="pdfViewerContainer"
                onLoadError={() => setRenderError(true)}
              >
                <div className="pdfMainViewer">
                  {memoizedFile && !loading && <Page pageNumber={pageNumber} className="pdfPage" width={pageWidth} />}
                </div>
                <div className="thumbnailSidebar">
                  {Array.from(new Array(numPages), (el, index) => (
                    <div
                      key={index}
                      className={`thumbnail ${index + 1 === pageNumber ? "active" : ""}`}
                      onClick={() => setPageNumber(index + 1)}
                    >
                      <Page pageNumber={index + 1} width={100} />
                      <Box sx={{ width: "100%", textAlign: "center" }}>Page {index + 1}</Box>
                    </div>
                  ))}
                </div>
              </Document>
              <Grid container spacing={0}>
                <Grid item xs={2} sx={{ display: "flex", justifyContent: "start" }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      dispatch(
                        openWindow("Edit Content", "ManageContentWindow", [], { contentId: file._id }, "context")
                      );
                    }}
                  >
                    Edit Content
                  </Button>
                </Grid>
                <Grid item xs={2} sx={{ display: "flex", justifyContent: "end" }}>
                  <ButtonGroup size="small" variant="contained">
                    <Button
                      disabled={pageNumber === 1}
                      onClick={() => {
                        setPageNumber(1);
                      }}
                    >
                      <FirstPageIcon fontSize="small" />
                    </Button>
                    <Button
                      disabled={pageNumber - 1 < 1}
                      onClick={() => {
                        setPageNumber(pageNumber - 1);
                      }}
                    >
                      <ArrowBackIosIcon sx={{ width: "13px" }} />
                    </Button>
                  </ButtonGroup>
                </Grid>
                <Grid item xs={2} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <TextField
                    sx={(theme) => ({
                      width: "60px",
                      border: `1px solid ${theme.palette.grey[600]}`,
                      marginRight: "5px",
                      "& .MuiInputBase-input": {
                        paddingRight: "5px",
                        fontSize: "0.9em",
                        textAlign: "center",
                        m: 0,
                        p: 0,
                      },
                    })}
                    size="small"
                    type="number"
                    variant="outlined"
                    value={pageNumber}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      if (!isNaN(newValue) && newValue >= 1 && newValue <= numPages) {
                        setPageNumber(newValue);
                      }
                    }}
                  />
                  / {numPages}
                </Grid>
                <Grid item xs={4} sx={{ display: "flex", justifyContent: "start" }}>
                  <ButtonGroup size="small" variant="contained">
                    <Button
                      disabled={pageNumber + 1 > numPages}
                      onClick={() => {
                        setPageNumber(pageNumber + 1);
                      }}
                    >
                      <ArrowForwardIosIcon sx={{ width: "13px" }} />
                    </Button>

                    <Button
                      disabled={pageNumber === numPages}
                      onClick={() => {
                        setPageNumber(numPages);
                      }}
                    >
                      <LastPageIcon fontSize="small" />
                    </Button>
                  </ButtonGroup>
                </Grid>
              </Grid>
            </Box>
          )}
        </PDFBox>
      )}
      {loadError && (
        <Alert severity="error" variant="filled">
          {FILE_PREVIEW_ERROR_MESSAGE}
        </Alert>
      )}
    </>
  );
}
